// for more information about GCS multipart limitations:
// https://cloud.google.com/storage/quotas
// Up to 32 objects can be composed in a single composition request
// componentCount: 2,147,483,647
// There is a limit of 100 access control list entries (ACLs) per object
import { XMLBuilder } from 'fast-xml-parser';

import { AbortEndpointRequestError } from '@iconik-io/iconik-api/src/client/response';
import type { FilesMultiPartUploadURLSchemaNullable } from '@iconik-io/iconik-api/src/services/files';
import { unwrapResponseBody } from '@iconik-io/iconik-api/src/utils/unwrap_response_body';

import {
  AbortUploadError,
  CancelUploadError,
  FileSizeError,
  InvalidUploadObjectError,
  MultipartUploadChunkError,
  UninitializedChunkError,
} from '../../errors';
import {
  UPLOADER_CHUNK_STATUS_COMPLETED,
  UPLOADER_CHUNK_STATUS_FAILED,
  UPLOADER_CHUNK_STATUS_UPLOADING,
  UPLOADER_STATUS_COMPLETING,
} from '../../state';
import { chunkIntoSize } from '../../utils/chunk_into_size';
import { fitChunkSize } from '../../utils/fit_chunk_size';
import { unwrapHeaderValue } from '../../utils/unwrap_header_value';
import { MultipartUploader } from '../multipart_uploader';
import {
  GcsApiError,
  GcsComposeUnsupportedError,
  GcsInvalidComposeResponseError,
  handleGcsApiError,
} from './errors';

type ComposeItemObject = { Name?: string; Generation?: string };

type ComposeObject = {
  key?: string;
  generation?: string;
};

const MAX_GCS_COMPONENTS_NUMBER = 32;
const MAX_GCS_COMPOSE_OBJECTS_NUMBER = 1024;

const MAX_PER_PAGE = 100;

const FILE_SIZE_LIMIT = 5_000_000_000_000;

const xmlBuilder = new XMLBuilder({
  ignoreAttributes: false,
  format: true,
});

const xmlDeclaration = {
  '?xml': {
    '@_version': '1.0',
    '@_encoding': 'UTF-8',
    '@_standalone': 'yes',
  },
};

type UploadDetails = {
  delete_url: string;
  key: string;
  url: string;
};

type GcsChunkMeta = {
  uploadDetails: UploadDetails;
};

function assertUploadDetails(
  urlObject: FilesMultiPartUploadURLSchemaNullable,
): asserts urlObject is UploadDetails {
  if (!urlObject.delete_url) {
    throw new InvalidUploadObjectError(urlObject, 'Delete URL is not set');
  }
  if (!urlObject.key) {
    throw new InvalidUploadObjectError(urlObject, 'Key is not set');
  }
  if (!urlObject.url) {
    throw new InvalidUploadObjectError(urlObject, 'URL is not set');
  }
}

export class GcsUploader extends MultipartUploader<never, GcsChunkMeta> {
  #uploadObjectPages = new Map<number, Promise<UploadDetails[]>>();
  partsGroupNumber = 0;

  protected validate(): boolean {
    if (this.state.fileInfo.size > FILE_SIZE_LIMIT) {
      throw new FileSizeError(FILE_SIZE_LIMIT);
    }
    if (!this.endpoints.gcsComposeUrl) {
      throw new GcsComposeUnsupportedError();
    }
    return true;
  }

  #unwrapComposeUrlEndpoint() {
    const gcsComposeUrl = this.endpoints.gcsComposeUrl;
    if (!gcsComposeUrl) {
      throw new GcsComposeUnsupportedError();
    }
    return gcsComposeUrl;
  }

  #getUploadObjectPage = (chunkIndex: number) => {
    const page = Math.floor(chunkIndex / MAX_PER_PAGE);
    const cachedPage = this.#uploadObjectPages.get(page);
    if (cachedPage !== undefined) {
      return cachedPage;
    }

    this.emit('log', {
      level: 'debug',
      message: `Fetching page ${page}`,
      state: this.state,
    });

    const assetId = this.unwrapAssetId();
    const entityId = this.unwrapUploadEntityId();
    const pagePromise = unwrapResponseBody(
      this.apiClient.request(
        this.endpoints.multipartUrlPart(assetId, entityId, {
          parts_num: this.unwrapChunks().length,
          per_page: MAX_PER_PAGE,
          page: page + 1,
        }),
        {
          abortSignal: this.abortList.newSignal(),
        },
      ),
    ).then(({ objects }) =>
      objects.map((object) => {
        assertUploadDetails(object);
        return object;
      }),
    );
    this.#uploadObjectPages.set(page, pagePromise);
    return pagePromise;
  };

  #getUploadObject = async (chunkIndex: number) => {
    const uploadObjects = await this.#getUploadObjectPage(chunkIndex);
    const indexInPage = chunkIndex % MAX_PER_PAGE;
    return uploadObjects[indexInPage];
  };

  #uploadChunkIndex = async (chunkIndex: number): Promise<void> => {
    const chunk = this.unwrapChunks()[chunkIndex];
    if (!chunk) {
      throw new UninitializedChunkError(chunkIndex);
    }

    const uploadDetails = await this.#getUploadObject(chunkIndex);

    this.updateChunks([
      {
        index: chunkIndex,
        status: UPLOADER_CHUNK_STATUS_UPLOADING,
        meta: { uploadDetails },
      },
    ]);

    return this.httpClient
      .request(
        {
          method: 'post',
          url: uploadDetails.url,
          headers: { 'x-goog-resumable': 'start' },
        },
        {
          abortSignal: this.abortList.newSignal(),
          retryOptions: { method: ['post'] },
        },
      )
      .then(async (response) => {
        const location = unwrapHeaderValue(response, 'location');
        const body = await this.readChunkData(
          chunkIndex,
          this.abortList.newSignal(),
        );
        return this.httpClient.request(
          {
            method: 'put',
            url: location,
            body,
          },
          {
            onUploadProgress: (event) =>
              this.uploadChunkProgress(chunkIndex, event),
            abortSignal: this.abortList.newSignal(),
          },
        );
      })
      .catch(handleGcsApiError)
      .then(() => {
        this.updateChunks([
          {
            index: chunkIndex,
            status: UPLOADER_CHUNK_STATUS_COMPLETED,
          },
        ]);
      })
      .catch((error) => {
        if (
          error instanceof AbortUploadError ||
          error instanceof AbortEndpointRequestError
        ) {
          throw error;
        }
        this.updateChunks([
          {
            index: chunkIndex,
            status: UPLOADER_CHUNK_STATUS_FAILED,
          },
        ]);
        throw new MultipartUploadChunkError(chunk, error);
      });
  };

  #getComposeBody = (parts: ComposeObject[]): string => {
    const components = parts.map((componentObject) => {
      const component: ComposeItemObject = { Name: componentObject.key };
      if (componentObject.generation) {
        component.Generation = componentObject.generation;
      }
      return component;
    });

    const xmlObject = {
      ...xmlDeclaration,
      ComposeRequest: {
        Component: components,
      },
    };

    return xmlBuilder.build(xmlObject);
  };

  /**
   * Compose a number of chunks into one. These chunks can then be composed in turn.
   * @param composeObjects Objects to compose together
   * @param partsGroup Index of the group that we are composing
   * @returns Data to be able to compose the result with other results of compose
   */
  #composeRequest = async (
    composeObjects: ComposeObject[],
    partsGroup: number | null = null,
  ): Promise<ComposeObject> => {
    this.emit('log', {
      level: 'info',
      message: `Composing ${composeObjects.length} chunks with group index ${partsGroup}`,
      state: this.state,
    });

    const assetId = this.unwrapAssetId();
    const entityId = this.unwrapUploadEntityId();
    const composeResponse = await this.apiClient.request(
      this.#unwrapComposeUrlEndpoint()(assetId, entityId, {
        parts_group: partsGroup,
        content_type: 'application/octet-stream',
      }),
      {
        abortSignal: this.abortList.newSignal(),
      },
    );
    const { compose_url: composeUrl, key } = composeResponse.body;
    if (!composeUrl) {
      throw new GcsInvalidComposeResponseError('Compose URL is empty');
    }
    if (!key) {
      throw new GcsInvalidComposeResponseError('Compose key is empty');
    }
    this.partsGroupNumber += 1;
    const response = await this.httpClient
      .request(
        {
          headers: {
            'content-type': 'application/octet-stream',
          },
          url: composeUrl,
          method: 'put',
          body: this.#getComposeBody(composeObjects),
        },
        {
          abortSignal: this.abortList.newSignal(),
        },
      )
      .catch(handleGcsApiError);
    const generation = unwrapHeaderValue(response, 'x-goog-generation');
    return { generation, key };
  };

  /**
   * When composing a number of uploaded chunks into one, GCS allows composing at most 32 chunks
   * in one call. If we have more than 32 chunks we need to do this in steps. So group the
   * chunks we have into groups of 32 chunks and compose those separately. Once that is done
   * take the result of all those compositions and compose those into the final file.
   * @param objects All chunks of the upload
   */
  #composeParts = async (objects: ComposeObject[]): Promise<void> => {
    this.emit('log', {
      level: 'debug',
      message: `Composing parts. ${objects.length} chunks in total.`,
      state: this.state,
    });

    const composeParts = chunkIntoSize(objects, MAX_GCS_COMPONENTS_NUMBER);
    const composeObjects = await Promise.all(
      composeParts.map((part, i) => this.#composeRequest(part, i)),
    );

    await this.#composeRequest(composeObjects);
  };

  checkIfUploadExpired(_assetId: string, _entityId: string): Promise<boolean> {
    return Promise.resolve(false);
  }

  protected async runUpload(): Promise<void> {
    this.#uploadObjectPages.clear();

    const chunkUploaders = this.getIncompleteChunks().map(
      (chunk) => () => this.#uploadChunkIndex(chunk.index),
    );
    this.emit('log', {
      level: 'info',
      message: `Found ${chunkUploaders.length} incomplete chunks to upload.`,
      state: this.state,
    });
    await this.chunksQueue.addAll(chunkUploaders);

    await this.#complete();
  }

  async #complete(): Promise<void> {
    this.updateState({ status: UPLOADER_STATUS_COMPLETING });

    this.emit('log', {
      level: 'info',
      message: `Completed uploading ${this.state.fileInfo.targetPath}`,
      state: this.state,
    });

    const composeObjects: ComposeObject[] = this.unwrapChunks().map(
      (chunk): ComposeObject => {
        const meta = this.unwrapChunkMeta(chunk.index);
        return {
          key: meta.uploadDetails.key,
        };
      },
    );

    // You can compose at most 32 chunks in one call so if we have more than 32 chunks we need
    // to compose in steps.
    if (this.unwrapChunks().length > MAX_GCS_COMPONENTS_NUMBER) {
      await this.#composeParts(composeObjects);
    } else {
      await this.#composeRequest(composeObjects, null);
    }
    this.emit('log', {
      level: 'debug',
      message: `Composed parts. Cleaning up.`,
      state: this.state,
    });

    const assetId = this.unwrapAssetId();
    const entityId = this.unwrapUploadEntityId();

    await this.apiClient.request(
      this.endpoints.multipartCleanup(assetId, entityId, {
        parts_group_number:
          this.partsGroupNumber - 1 /* all groups but the last one */,
        parts_number: this.unwrapChunks().length,
      }),
      {
        abortSignal: this.abortList.newSignal(),
      },
    );
  }

  protected getChunkSize() {
    // GCS has a limitation that you can have at most 1024 chunks. So if using the default
    // chunk size will result in more chunks for the file we are uploading we recalculate
    // the chunk size so that it fits in at most MAX_GCS_COMPOSE_OBJECTS_NUMBER chunks.
    return fitChunkSize(
      this.state.fileInfo.size,
      super.getChunkSize(),
      MAX_GCS_COMPOSE_OBJECTS_NUMBER,
    );
  }

  protected async runCancel(): Promise<void> {
    if (!this.state.chunks) {
      return;
    }

    await Promise.all(
      this.state.chunks
        .map((chunk) => chunk.meta)
        .filter((chunkMeta): chunkMeta is GcsChunkMeta => Boolean(chunkMeta))
        .map(async (chunkMeta) => {
          await this.httpClient
            .request(
              {
                method: 'delete',
                url: chunkMeta.uploadDetails.delete_url,
              },
              // No abort signal: cancelling aborts the abort list first, so a
              // signal from it would cancel this request the moment it is issued.
              {},
            )
            .catch(handleGcsApiError)
            .catch((error) => {
              // This just means that the chunk was not found so it's already gone
              if (
                error instanceof GcsApiError &&
                error.errorInfo.code === 'NoSuchKey'
              ) {
                return;
              }
              throw new CancelUploadError(error);
            });
        }),
    );
  }
}
